import { useCallback, useEffect, useMemo, useState } from 'react';
import { ExamDatesForLocationDto, ExamDatesForLocationItem, ActionEnum } from '../common/types';
import {
  DateAvailability,
  SessionDateAvailabilityDto,
  SessionDateAvailabilityRequest,
  DistrictConfigurationDto,
} from 'api/planning';
import {
  useAllowedActions,
  useNavigationContext,
  useCentreConfiguration,
  useSafeMutation,
} from 'hooks';
import { examDatesForLocationsStore } from 'store';
import { formatApiDate, parseApiDate } from 'utils';
import {
  compareDateOnly,
  compareMonthAndYearOnly,
  getFirstDayOfCurrentMonth,
  getNewState,
} from '../common/utilities';

export const useExamDatesForLocationsCentreContext = () => {
  const { canEditExamDatesForLocations } = useAllowedActions();
  const { centreId } = useNavigationContext();
  const [tableData, setTableData] = useState<ExamDatesForLocationDto[]>([]);
  const [date, setDate] = useState(getFirstDayOfCurrentMonth());
  const [isLoadingButtons, setIsLoadingButtons] = useState(false);

  const isModified = useMemo(
    () =>
      tableData.some((locationDto) =>
        locationDto.dates.some((dateItem) => dateItem.action !== ActionEnum.None)
      ),
    [tableData]
  );

  const { data, isFetching: isDataLoading } =
    examDatesForLocationsStore.service.useGetSessionDateAvailabilitiesCentreContextQuery({
      year: date.getFullYear(),
      month: date.getMonth() + 1,
      centreId,
    });

  const [saveSessionDateAvailabilities] =
    examDatesForLocationsStore.service.useSaveSessionDateAvailabilitiesMutation();

  const { execute: saveChanges } = useSafeMutation(saveSessionDateAvailabilities, {
    successToastMessage: 'Changes saved successfully',
    onSuccess: () => setIsLoadingButtons(false),
    onError: () => setIsLoadingButtons(false),
  });

  const {
    isIeltsFlexiblePlanningOnCentreEnabled,
    isUkviFlexiblePlanningOnCentreEnabled,
    districtConfigurations,
  } = useCentreConfiguration();

  const mergeDistrictDates = (
    prevDistrictData: ExamDatesForLocationDto,
    items: ExamDatesForLocationDto[]
  ): ExamDatesForLocationDto => {
    const newDistrictData = items.find((i) => i.locationId == prevDistrictData.locationId)!;
    return {
      ...prevDistrictData,
      dates: prevDistrictData.dates
        .filter(
          (prevDate) =>
            !newDistrictData.dates.some((newDate) => compareDateOnly(newDate.date, prevDate.date))
        )
        .concat(newDistrictData.dates),
    };
  };

  const generateExamDatesForSelectedDate = useCallback(
    (
      selectedDate: Date,
      data: SessionDateAvailabilityDto[] | undefined,
      districtConfigurations: DistrictConfigurationDto[],
      isIeltsFlexiblePlanningOnCentreEnabled: boolean,
      isUkviFlexiblePlanningOnCentreEnabled: boolean,
      canEditExamDatesForLocations: boolean
    ) => {
      if (!data) {
        return [];
      }
      const year = selectedDate.getFullYear();
      const month = selectedDate.getMonth();

      const item: ExamDatesForLocationDto[] = Array.from({ length: data.length }, (_, index_x) => {
        const daysInMonth = new Date(year, month + 1, 0).getDate();
        const sessionDateAvailabilty = data[index_x];
        const dates: ExamDatesForLocationItem[] = Array.from(
          { length: daysInMonth },
          (_, index_y) => {
            const date = new Date(year, month, index_y + 1);

            const available = sessionDateAvailabilty.dates.find((x) =>
              compareDateOnly(parseApiDate(x.date), date)
            );
            return {
              date,
              active: available !== undefined,
              action: ActionEnum.None,
            };
          }
        );

        return {
          locationId: data[index_x].districtId,
          location: data[index_x].districtName,
          dates: dates,
          isEditable:
            canEditExamDatesForLocations &&
            (isIeltsFlexiblePlanningOnCentreEnabled ||
              isUkviFlexiblePlanningOnCentreEnabled ||
              districtConfigurations.some(
                (district) =>
                  district.districtId == sessionDateAvailabilty.districtId &&
                  (district.isIeltsFlexiblePlanningEnabled ||
                    district.isUkviFlexiblePlanningEnabled)
              )),
        };
      });

      setTableData((prevData) => {
        if (!prevData || prevData.length == 0) {
          return item;
        }
        return prevData.map((prevDistrictData) => mergeDistrictDates(prevDistrictData, item));
      });

      setIsLoadingButtons(false);
    },
    []
  );

  useEffect(() => {
    generateExamDatesForSelectedDate(
      date,
      data,
      districtConfigurations,
      isIeltsFlexiblePlanningOnCentreEnabled,
      isUkviFlexiblePlanningOnCentreEnabled,
      canEditExamDatesForLocations
    );
  }, [
    date,
    data,
    generateExamDatesForSelectedDate,
    districtConfigurations,
    isIeltsFlexiblePlanningOnCentreEnabled,
    isUkviFlexiblePlanningOnCentreEnabled,
    canEditExamDatesForLocations,
  ]);

  const clearDateAction = (d: ExamDatesForLocationItem): ExamDatesForLocationItem => ({
    ...d,
    action: ActionEnum.None,
  });

  const clearDistrictActions = (
    prevDistrictData: ExamDatesForLocationDto
  ): ExamDatesForLocationDto => ({
    ...prevDistrictData,
    dates: prevDistrictData.dates.map(clearDateAction),
  });

  const onSave = () => {
    const dateToCreate: DateAvailability[] = [];
    const dateToDelete: DateAvailability[] = [];

    for (const locationData of tableData) {
      for (const dateItem of locationData.dates) {
        if (dateItem.action === ActionEnum.Added)
          dateToCreate.push({
            districtId: locationData.locationId,
            date: formatApiDate(dateItem.date),
          });
        if (dateItem.action === ActionEnum.Deleted)
          dateToDelete.push({
            districtId: locationData.locationId,
            date: formatApiDate(dateItem.date),
          });
      }
    }

    const SessionDateAvailabilityRequest: SessionDateAvailabilityRequest = {
      dateToCreate: dateToCreate,
      dateToDelete: dateToDelete,
    };

    setTableData((prevData) => prevData.map(clearDistrictActions));
    setIsLoadingButtons(true);
    saveChanges(SessionDateAvailabilityRequest);
  };

  const resetDistrictDates = (
    prevDistrictData: ExamDatesForLocationDto
  ): ExamDatesForLocationDto => {
    return {
      ...prevDistrictData,
      dates: prevDistrictData.dates.map(resetDateItem),
    };
  };

  const resetDateItem = (d: ExamDatesForLocationItem): ExamDatesForLocationItem => {
    return {
      date: d.date,
      active: d.action == ActionEnum.None ? d.active : !d.active,
      action: ActionEnum.None,
    };
  };

  const onDiscard = () => {
    setTableData((prevData) => prevData.map(resetDistrictDates));
  };

  const onDateChange = useCallback(
    (newDate: Date | null) => {
      if (newDate) {
        const normalizedDate = new Date(newDate.getFullYear(), newDate.getMonth(), 1);
        if (!compareMonthAndYearOnly(date, normalizedDate)) {
          setDate(normalizedDate);
        }
      }
    },
    [date]
  );

  const onChange = useCallback(
    (locationIndex: number, dateIndex: number) => {
      setTableData((prevData) =>
        prevData.map((location, index) => {
          return updateLocationDates(location, index, locationIndex, dateIndex, date);
        })
      );
    },
    [date]
  );

  const updateLocationDates = (
    location: ExamDatesForLocationDto,
    locationIndex: number,
    targetLocationIndex: number,
    dateIndex: number,
    date: Date
  ): ExamDatesForLocationDto => {
    if (locationIndex !== targetLocationIndex) return location;

    const newDates = [...location.dates];
    const changedDate = new Date(date.getFullYear(), date.getMonth(), dateIndex + 1);
    const idx = newDates.findIndex((d) => compareDateOnly(d.date, changedDate));
    if (idx < 0) return location;
    newDates[idx] = getNewState(newDates[idx]);
    return { ...location, dates: newDates };
  };

  return {
    date,
    tableData,
    isDataLoading,
    isLoadingButtons,
    isModified,
    onSave,
    onDiscard,
    onDateChange,
    onChange,
  };
};
